// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The toy (M3-07, 05 §3).
//
// > "Every great 1990s machine has one thing on the playfield that moves and that you look at."
//
// NEON NOVA's is a raised translucent dome at the centre-upper playfield with the SINGULARITY
// magnet beneath it and the EVENT HORIZON scoop cut into its base. What matters is the sentence
// after that one: **the star is a state display, not decoration.** Its appearance is the Star
// Core charge, the collapse stage and whether BIG BANG is running — three numbers the rules
// already own — so nothing here decides what the toy should look like. It reads.
//
// **Declared as data, in the machine definition.** A dome hard-coded here would be NEON NOVA
// living in the renderer, which is the one thing `core/` and the machine format exist to
// prevent: a Builder machine (M6) gets a toy by writing one, not by getting a release.
//
// Three things are load-bearing and none is obvious:
//
// **The animation is driven by the simulation step, never by a clock.** One step is one
// millisecond at PH's fixed 1000 Hz, so `holdMs` on the climax is a step count with no
// conversion and no drift, and the dome breathes at the same rate on a phone at 30 fps as on a
// headset at 90 Hz. A toy animated off `performance.now()` would drift away from the game
// driving it, and the tell is subtle and maddening: the star finishes collapsing slightly before
// or after the shot that collapsed it.
//
// **Nothing here flashes.** NFR-026 puts 3 Hz to 55 Hz out of bounds at high contrast, and this
// is the brightest object on the playfield. The format caps `pulseHz` below 3 in the validator;
// the flicker is two slow incommensurate sines rather than noise, so it wanders instead of
// strobing; and the corona's opacity is a half-sine over its travel, which has no
// discontinuity at the wrap — a ring that vanished at the end of its journey would be a flash
// at the ring rate, however slow the ring rate was.
//
// **Every appearance differs by more than colour** (NFR-021): the four charge bands differ in
// pulse rate and in how many corona rings stand off the dome, and the collapse differs in
// *size*. A player who cannot tell violet from magenta can still count the rings, and size is
// the cue that survives a phone at arm's length.
//
// Emissive geometry and **no dynamic light** (ARCH-011). Five meshes — the shell, the core and
// three corona rings, of which only the lit ones are visible. 05 §3 says "one draw call"; five
// is what a shell, a core and independently-travelling rings actually cost without a custom
// shader, and the claim worth keeping is the one about lights.

import {
  Float32BufferAttribute,
  SRGBColorSpace,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  TorusGeometry,
  BufferGeometry,
  type Material,
} from 'three'
import type { MachineDefinition, MachineToy, ToyStage } from '@core/machine'
import { parseColour } from './lamps'
import { fittedToy,toyAssemblyProfile } from './toyAssembly'
import {toyOpticalSurface,toyLightguide} from './toyOptics'
import {buildSalvageMechanism} from './salvageMechanisms'

/** Steps per second. One step is one millisecond, which is why `holdMs` needs no conversion. */
const STEPS_PER_SECOND = 1000

/** The most corona rings the format allows, and therefore the most meshes worth building. */
export const MAX_CORONA_RINGS = 3

/** How far a corona ring travels, as a multiple of the dome radius. */
export const CORONA_TRAVEL = 0.65

/** Rings complete a journey this often, in hertz. Well under NFR-026's floor. */
export const CORONA_HZ = 0.45

/** The two flicker components, in hertz. Incommensurate, so they beat rather than repeat. */
export const FLICKER_HZ = [1.7, 2.3] as const

/** What the toy needs to know about the game, read once a frame. */
export interface ToyStateReader {
  /** A rule variable's value for the player at the machine. */
  value: (id: string) => number
  /** Is this mode running right now? */
  modeActive: (id: string) => boolean
}

/** One corona ring's state this frame. */
export interface CoronaRing {
  /** Multiple of the dome radius. */
  readonly scale: number
  readonly opacity: number
}

/** Everything the meshes need, and nothing about meshes. Pure, so it can be tested. */
export interface ToyAppearance {
  /** Linear RGB, 0…1. */
  readonly colour: readonly [number, number, number]
  /** How hard the dome is lit, 0…1. */
  readonly brightness: number
  /** Multiple of the declared radius. Below 1 while the star is collapsing. */
  readonly radiusScale: number
  readonly rings: readonly CoronaRing[]
  /** BIG BANG, 0…1: white out the dome, blow the corona outward, cut the illumination. */
  readonly whiteout: number
}

/** What the toy is being told, gathered before the arithmetic so the arithmetic stays pure. */
export interface ToyInput {
  readonly charge: number
  /** How far through the collapse, 0…1. */
  readonly collapse: number
  /** Steps since the climax began, or −1 when it is not running. */
  readonly climaxSteps: number
  readonly step: number
  /**
   * The player's motion budget, 0…1, from `MotionPreference`.
   *
   * Zero means somebody asked not to be moved about. The toy is a *state display*, so at zero it
   * does not go dark or go still-and-blank: it holds the middle of its pulse, keeps its rings
   * where they are, and stops travelling. Every cue that carries information survives; only the
   * movement stops.
   */
  readonly motion: number
}

/**
 * Which appearance a charge selects.
 *
 * The last stage whose `from` the value has reached — which is why the validator insists the
 * stages ascend. Declared out of order, this would silently pick one the author never meant.
 */
export function stageFor(stages: readonly ToyStage[], charge: number): ToyStage | undefined {
  let chosen: ToyStage | undefined
  for (const stage of stages) {
    if (charge >= stage.from) chosen = stage
    else break
  }
  return chosen ?? stages[0]
}

/**
 * The whole appearance, as numbers.
 *
 * Separated from the meshes for the reason `balls.ts` is: everything here is arithmetic that is
 * easy to get wrong, invisible in a screenshot when it is, and testable in a second without a
 * screen.
 */
export function toyAppearance(toy: MachineToy, input: ToyInput): ToyAppearance {
  const stage = stageFor(toy.stages, input.charge)
  const rgb = parseColour(stage?.colour ?? '#FFFFFF')
  const seconds = input.step / STEPS_PER_SECOND

  // The breath. Held at its midpoint under reduced motion rather than at its peak: a dome
  // pinned bright is a different appearance from the one every other player sees.
  const hz = stage?.pulseHz ?? 0
  const swing = input.motion * 0.35
  const breath = hz > 0 ? Math.sin(2 * Math.PI * hz * seconds) : 0
  let brightness = 0.55 + swing * breath

  // The instability. Two slow sines rather than noise, so it wanders at a rate an eye reads as
  // "unstable" and never at one NFR-026 forbids.
  const flicker = (stage?.flicker ?? 0) * input.motion
  if (flicker > 0) {
    const a = Math.sin(2 * Math.PI * FLICKER_HZ[0] * seconds)
    const b = Math.sin(2 * Math.PI * FLICKER_HZ[1] * seconds + 1.7)
    brightness += flicker * 0.22 * (a * 0.6 + b * 0.4)
  }

  // Collapsing brightens as it shrinks — the same light in a smaller star.
  brightness *= 1 + 0.55 * input.collapse
  const radiusScale = 1 - (1 - (toy.collapse?.shrinkTo ?? 1)) * input.collapse

  const hold = toy.climax?.holdMs ?? 0
  const whiteout =
    input.climaxSteps < 0 || hold <= 0 ? 0 : Math.max(0, 1 - input.climaxSteps / hold)

  const count = Math.min(stage?.coronaRings ?? 0, MAX_CORONA_RINGS)
  const rings: CoronaRing[] = []
  for (let i = 0; i < count; i++) {
    // Evenly spaced around one journey, so the rings arrive in a procession rather than
    // together. Frozen in place under reduced motion, which still shows how many there are.
    const spacing = i / count
    const travel = input.motion === 0 ? spacing : frac(seconds * CORONA_HZ * input.motion + spacing)
    const u = stage?.collapsing === true ? 1 - travel : travel
    // A half-sine: zero at both ends of the journey, so there is no step change when it wraps.
    const opacity = 0.75 * Math.sin(Math.PI * travel)
    rings.push({
      scale: 1 + CORONA_TRAVEL * u + whiteout * 1.4,
      opacity: Math.max(opacity, whiteout * 0.9),
    })
  }

  const white = (channel: number): number => channel + (1 - channel) * whiteout
  return {
    colour: [white(rgb[0] / 255), white(rgb[1] / 255), white(rgb[2] / 255)],
    brightness: clamp01(Math.max(brightness, whiteout)),
    radiusScale,
    rings,
    whiteout,
  }
}

export interface BuiltToys {
  readonly group: Group
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  readonly skipped: readonly string[]
  /** How far the playfield illumination is cut this frame, 0…1 (05 §3's BIG BANG). */
  readonly dim: () => number
  readonly update: (read: ToyStateReader, step: number, motion: number) => void
}

/** Build every toy the machine declares. */
export function buildToys(definition: MachineDefinition, motionAtBuild = 1): BuiltToys {
  const group = new Group()
  group.name = 'toys'
  const geometries: BufferGeometry[] = []
  const materials: Material[] = []
  const skipped: string[] = []
  const built: BuiltToy[] = []

  for (const toy of definition.toys ?? []) {
    if(toy.kind==='hull'||toy.kind==='portal'){
      const one=buildSalvageMechanism(toy);geometries.push(...one.geometries);materials.push(...one.materials);group.add(one.group);built.push(one);continue
    }
    if (toy.kind !== 'dome') {
      // Unreachable while `dome` is the only kind, and kept because the next kind will arrive
      // in a definition before it arrives here — and a toy drawn as nothing with no explanation
      // is exactly the class of thing `undrawn` exists to surface.
      skipped.push(`${(toy as { id: string }).id}: no geometry for a toy of this kind`)
      continue
    }
    const one = buildDome(toy)
    geometries.push(...one.geometries)
    materials.push(...one.materials)
    group.add(one.group)
    built.push(one)
  }

  let dim = 0
  const update = (read: ToyStateReader, step: number, motion: number): void => {
    let cut = 0
    for (const one of built) cut = Math.max(cut, one.update(read, step, motion))
    dim = cut
  }
  update(idleReader(), 0, motionAtBuild)

  return { group, geometries, materials, skipped, dim: () => dim, update }
}

interface BuiltToy {
  readonly group: Group
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  /** Returns this toy's illumination cut, 0…1. */
  readonly update: (read: ToyStateReader, step: number, motion: number) => number
}

function buildDome(toy: MachineToy): BuiltToy {
  const group = new Group()
  group.name = toy.id
  group.position.set(toy.centre[0], toy.centre[1], 0)

  const geometries: BufferGeometry[] = []
  const materials: Material[] = []

  const parts=fittedToy(toy.radius),profile=toyAssemblyProfile()
  const authored=(role:string,fallback:()=>BufferGeometry):BufferGeometry=>{
    const p=parts?.find(p=>p.role===role);if(p===undefined)return fallback()
    const g=new BufferGeometry();g.setAttribute('position',new Float32BufferAttribute(p.positions,3));g.setAttribute('normal',new Float32BufferAttribute(p.normals,3));g.setAttribute('uv',new Float32BufferAttribute(p.uvs,2));g.setIndex(p.triangles);return g
  }
  const shellGeometry=authored('shell',()=>new SphereGeometry(1,28,14,0,Math.PI*2,0,Math.PI/2).rotateX(Math.PI/2));geometries.push(shellGeometry)
  const shellMaterial=toyOpticalSurface('shell',.035);materials.push(shellMaterial)
  const shell=new Mesh(shellGeometry,shellMaterial);shell.name=toy.id+'.shell';group.add(shell)
  const coreGeometry=authored('core',()=>new SphereGeometry(1,20,12));geometries.push(coreGeometry)
  const coreMaterial=toyOpticalSurface('core',.20);materials.push(coreMaterial)
  const core=new Mesh(coreGeometry,coreMaterial);core.name=toy.id+'.core';group.add(core)
  const glowPart=parts?.find(p=>p.role==='coreGlow')
  const glowGeometry=glowPart===undefined?undefined:authored('coreGlow',()=>new BufferGeometry())
  const glowMaterial=glowGeometry===undefined?undefined:toyLightguide('coreGlow')
  const glow=glowGeometry===undefined||glowMaterial===undefined?undefined:new Mesh(glowGeometry,glowMaterial)
  if(glow!==undefined){glow.name=toy.id+'.coreGlow';group.add(glow);geometries.push(glowGeometry!);materials.push(glowMaterial!)}

  const ringMeshes: Mesh[] = []
  for (let i = 0; i < MAX_CORONA_RINGS; i++) {
    // Lying in the playfield plane, which is this group's XY: a corona seen from the player's
    // eye is a ring around the base, not a halo behind it.
    const geometry = authored('ring',()=>new TorusGeometry(toy.radius, toy.radius * 0.045, 8, 40))
    geometries.push(geometry)
    const material=toyLightguide('ring')
    materials.push(material)
    const mesh = new Mesh(geometry, material)
    mesh.name=toy.id+'.ring'+i
    mesh.position.z = 0.004
    mesh.visible = false
    group.add(mesh)
    ringMeshes.push(mesh)
  }

  const colour = new Color()
  /**
   * When this toy's climax began, or nothing.
   *
   * Held in the closure rather than in a module-level map keyed by id, which is the version
   * this was first written as: two machines on screen at once — the arcade's roster of live
   * attract tiles (M8) is exactly that — would share one entry and fire each other's flash.
   */
  let climaxStartedAt: number | undefined

  /**
   * How many steps the climax has been running, or −1.
   *
   * The start is remembered here because a mode's start is not something the renderer is told,
   * and it only has to be right to the frame: one step is one millisecond, so a 400 ms flash is
   * 400 of them and `holdMs` needs no conversion at all.
   */
  const climaxProgress = (read: ToyStateReader, step: number): number => {
    const climax = toy.climax
    if (climax === undefined) return -1
    if (!read.modeActive(climax.mode)) {
      climaxStartedAt = undefined
      return -1
    }
    if (climaxStartedAt === undefined) {
      climaxStartedAt = step
      return 0
    }
    // A machine rebuilt mid-mode restarts its step index, and a negative age would run the
    // flash backwards — the star imploding on a jackpot.
    return Math.max(0, step - climaxStartedAt)
  }

  const update = (read: ToyStateReader, step: number, motion: number): number => {
    const charge = read.value(toy.chargeVar)
    const collapseSpec = toy.collapse
    const collapse =
      collapseSpec === undefined
        ? 0
        : clamp01(read.value(collapseSpec.var) / Math.max(1, collapseSpec.stages))
    const climaxSteps = climaxProgress(read, step)
    const look = toyAppearance(toy, { charge, collapse, climaxSteps, step, motion })

    colour.setRGB(look.colour[0], look.colour[1], look.colour[2],SRGBColorSpace)
    const radius = toy.radius * look.radiusScale

    const shellScale=profile?.shellFollowsCollapse===false?1:look.radiusScale
    shell.scale.set(toy.radius*shellScale,toy.radius*shellScale,toy.height*shellScale)
    shellMaterial.color.copy(colour).multiplyScalar(.07)

    const coreRadius = radius * 0.5
    core.scale.set(coreRadius,coreRadius,toy.height*look.radiusScale*.5)
    core.position.z=toy.height*(look.radiusScale*.45+(profile?.coreCollapsePivotHeight??0)*(1-look.radiusScale))
    coreMaterial.color.copy(colour).multiplyScalar(.18).addScalar(.10)
    if(glow!==undefined&&glowMaterial!==undefined){
      glow.scale.copy(core.scale);glow.position.copy(core.position)
      glowMaterial.color.copy(colour);glowMaterial.opacity=.95*look.brightness
    }

    for (let i = 0; i < ringMeshes.length; i++) {
      const mesh = ringMeshes[i]!
      const ring = look.rings[i]
      if (ring === undefined || ring.opacity <= 0.002) {
        mesh.visible = false
        continue
      }
      mesh.visible = true
      mesh.scale.setScalar(ring.scale * look.radiusScale)
      const material = mesh.material as MeshBasicMaterial
      material.color.copy(colour)
      material.opacity = ring.opacity*.55
    }

    return look.whiteout
  }

  return { group, geometries, materials, update }
}

/** A reader that answers nothing, for the first paint before a game exists. */
function idleReader(): ToyStateReader {
  return { value: () => 0, modeActive: () => false }
}

function frac(v: number): number {
  return v - Math.floor(v)
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}
